import {
    Component,
    ElementRef,
    EventEmitter,
    Inject,
    Input,
    NgZone,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
    TemplateRef,
    ViewChild,
} from '@angular/core';
import { NgbModal, NgbModalRef } from '@cmps/ng-bootstrap';
import jss, { StyleSheet } from 'jss';
import { Store } from 'redux';
import {
    animationFrameScheduler,
    from,
    of,
    scheduled,
    Subscription,
} from 'rxjs';
import { finalize, tap } from 'rxjs/operators';

import { AuthService } from '../auth/auth.service';
import {
    ConfigPath,
    ConfigViewPillar,
    QuickSwitchType,
} from '../insight/config.insight.model';
import { CustomizeColumnsInsightChangeEvent } from '../insight/customize-columns.insight.model';
import { CustomizeMetricsInsightChangeEvent } from '../insight/customize-metrics.insight.model';
import {
    CustomizePillarsInsightCanSelectFn,
    CustomizePillarsInsightChangeEvent,
} from '../insight/customize-pillars.insight.model';
import { ComponentType } from '../insight/enums/component-type.enum';
import { Tab } from '../insight/experimental.tabset';
import {
    GridInsightNewTabClickEvent,
    GridInsightSelectionChangeEvent,
    GridInsightSelectionItem,
} from '../insight/grid.insight.model';
import {
    gridExport,
    periodSelectEventDate,
    periodSelectPeriodType,
    rootClearSelectedVisualItem,
    rootListDefinitionSummaryCancel,
    rootReloadConfig,
    rootRetrieveConfig,
    rootSelectMetrics,
    rootSelectOptionalColumns,
    rootSelectPath,
    rootSelectPillars,
    rootSelectPresentationView,
    rootSelectVisualItem,
    rootSetTrendHideFirstGridColumn,
    trendSelectPeriodType,
} from '../insight/insight.action';
import { CacheDateItem } from '../insight/performance-data/cache-date.pd.model';
import { PopUpService } from '../insight/pop-up.service';
import { PresentationView } from '../insight/presentation.insight.model';
import { ConfigureTab, TabContext } from '../insight/tabset.model';
import { VisualItemClickEvent } from '../insight/visual-item.insight.model';
import { StateToProps, storeObserver } from '../store';
import { STORE } from '../store.metadata';
import { PeriodType } from '../widget/date-range.widget.model';

import {
    LevelOptionLabel,
    LevelOptionSelectionChangeEvent,
} from './level-option.pi.model';
import { levelOptionSelect } from './performance-insight.action';
import { TranslationKeyMap } from './performance-insight.i18n';
import { PiTabConfig } from './performance-insight.model';
import { RootState, storeStateMapper } from './performance-insight.state';

// TODO de-dup and merge into epics
@Component({
    selector: '[cmpsPerformanceInsight]',
    templateUrl: './performance-insight.component.html',
})
export class PerformanceInsightComponent
    implements
        OnChanges,
        OnInit,
        OnDestroy,
        StateToProps<RootState>,
        ConfigureTab<PiTabConfig>
{
    @ViewChild('atLeastOnePillarDialogTpl', { static: true })
    private readonly _atLeastOnePillarDialogTpl: TemplateRef<any>;

    @Input() context: TabContext<PiTabConfig>;
    @Output() setDefault: EventEmitter<boolean>;
    @Output() setFavourite: EventEmitter<boolean>;
    @Output() addNewTab: EventEmitter<Tab<TabContext<PiTabConfig>>>;
    i18n = TranslationKeyMap;
    PresentationView = PresentationView;
    QuickSwitchType = QuickSwitchType;
    private _storeSub: Subscription;
    state: RootState;
    /** @internal**/ _sheet: StyleSheet;
    overlayBodyHeight: number;
    gridClassName: string;
    cacheDates: CacheDateItem[];
    gridSelection: GridInsightSelectionItem[];
    canSelectPillar: CustomizePillarsInsightCanSelectFn;
    private _modalRef: NgbModalRef;
    optsLabel: LevelOptionLabel;

    constructor(
        private readonly _ngZone: NgZone,
        private readonly _el: ElementRef,
        private readonly _modalService: NgbModal,
        @Inject(STORE) private readonly _store: Store,
        public popUpService: PopUpService,
        private readonly _authService: AuthService,
    ) {
        this.setDefault = new EventEmitter();
        this.setFavourite = new EventEmitter();
        this.canSelectPillar = (
            pillar: ConfigViewPillar,
            checked: boolean,
        ): boolean => {
            const pls = this.state.activePillars;
            const noDeselect =
                ((pls.length === 1 && pls[0] === pillar) ||
                    // eslint-disable-next-line @typescript-eslint/prefer-includes
                    (pls.length === 2 &&
                        pls[0].pillars &&
                        // eslint-disable-next-line @typescript-eslint/prefer-includes
                        pls[0].pillars.indexOf(pls[1]) !== -1 &&
                        // eslint-disable-next-line @typescript-eslint/prefer-includes
                        pls[0].pillars.indexOf(pillar) !== -1) ||
                    // eslint-disable-next-line @typescript-eslint/prefer-includes
                    (pls.length !== 0 &&
                        pls[0] === pillar &&
                        pls[0].pillars &&
                        pls
                            .slice(1)
                            // eslint-disable-next-line @typescript-eslint/prefer-includes
                            .every((e) => pls[0].pillars.indexOf(e) !== -1))) &&
                checked;
            if (noDeselect) {
                this._showAtLeastOnePillar();
                return false;
            }
            return true;
        };
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!this.state) return;
        // eslint-disable-next-line no-prototype-builtins
        if (changes.hasOwnProperty('context')) {
            this.propsToDispatch();
        }
    }

    ngOnInit(): void {
        this._storeSub = storeObserver<any, RootState>(
            this._store,
            storeStateMapper,
        )
            .pipe(
                tap((state) =>
                    this._ngZone.run(() => {
                        const prevState = this.state;
                        this.state = state;
                        this.stateToProps(prevState);
                    }),
                ),
            )
            .subscribe();

        this._sheet = jss.createStyleSheet({}, { link: true }).attach();
        this._sheet.addRule('underlay', {});
        this._sheet.addRule('overlay', {});
        this._sheet.addRule('overlayBody', {});
    }

    ngOnDestroy(): void {
        this._store.dispatch(rootListDefinitionSummaryCancel());
        if (this._modalRef) {
            this._modalRef.dismiss();
        }
        this.popUpService.hide();
        jss.removeStyleSheet(this._sheet);
        if (this._storeSub) {
            this._storeSub.unsubscribe();
        }
    }

    stateToProps(prevState?: RootState): void {
        if (!prevState) {
            this.propsToDispatch();
            return;
        }

        if (
            prevState.period.cacheDates !== this.state.period.cacheDates ||
            prevState.period.periodType !== this.state.period.periodType ||
            !this
                .cacheDates /* TODO revise. this is because of persistPeriod. see if persistPeriod should be kept */
        ) {
            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
            this.cacheDates =
                // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                this.state.period.cacheDates &&
                this.state.period.cacheDates.filter(
                    (e) => e.periodType === this.state.period.periodType,
                );
        }

        if (
            (prevState.pending.config && !this.state.pending.config) ||
            (prevState.pending.configReload && !this.state.pending.configReload)
        ) {
            this._store.dispatch(levelOptionSelect(null));
        }

        if (
            prevState.activePath !== this.state.activePath ||
            (prevState.pending.supportedColumns &&
                !this.state.pending.supportedColumns) ||
            prevState.presentAs !== this.state.presentAs ||
            prevState.trendHideFirstGridColumn !==
                this.state.trendHideFirstGridColumn
        ) {
            this._layout();
        }

        if (
            prevState.presentAs === PresentationView.TREND &&
            this.state.presentAs === PresentationView.TREND &&
            (prevState.activeMetrics !== this.state.activeMetrics ||
                prevState.trend.activeColumns !==
                    this.state.trend.activeColumns)
        ) {
            this._layout();
        }

        if (
            !prevState.pending.summary &&
            !this.state.pending.summary &&
            !prevState.pending.config &&
            !this.state.pending.config &&
            prevState.period.eventDate !== this.state.period.eventDate &&
            ((prevState.period.pending.periodType &&
                !this.state.period.pending.periodType) ||
                (!prevState.period.pending.periodType &&
                    !this.state.period.pending.periodType))
        ) {
            this._store.dispatch(
                rootReloadConfig(
                    this.context.config.identifier,
                    ComponentType.PI,
                    this._authService.user.id,
                ),
            );
        }
    }

    propsToDispatch(): void {
        this._store.dispatch(
            rootRetrieveConfig(
                this.context.config.identifier,
                ComponentType.PI,
                this._authService.user.id,
                false,
            ),
        );
    }

    private _layout(): void {
        scheduled(of(null), animationFrameScheduler).subscribe(() => {
            const underlay = this._el.nativeElement.querySelector(
                '.insight-underlay',
            ) as HTMLElement;
            if (underlay) {
                const underlayRule = this._sheet.getRule('underlay');
                const overlayRule = this._sheet.getRule('overlay');
                underlayRule.prop('height', null);
                let height = underlay.clientHeight;
                if (
                    this.state.presentAs === PresentationView.TREND &&
                    this.state.activeMetrics &&
                    this.state.trend.activeColumns &&
                    this.gridSelection
                ) {
                    const legendRowHeight = 30; // TODO revise
                    const itemsPerRow = 2; // TODO revise
                    const maxRowsHeightBeforeExtend = legendRowHeight * 3;
                    const extendHeight =
                        ((this.state.activeMetrics.length *
                            this.state.trend.activeColumns.length *
                            this.gridSelection.length) /
                            itemsPerRow) *
                        legendRowHeight;
                    if (extendHeight > maxRowsHeightBeforeExtend) {
                        height += extendHeight;
                        underlayRule.prop('height', [
                            `${height}px`,
                            '!important',
                        ]);
                    }
                }
                overlayRule.prop('height', `${height}px`);
            }

            const overlayContent = this._el.nativeElement.querySelector(
                '.insight-overlay-content',
            ) as HTMLElement;
            const overlayHead = this._el.nativeElement.querySelector(
                '.insight-overlay-head',
            ) as HTMLElement;
            if (overlayContent && overlayHead) {
                const margin = parseInt(
                    window.getComputedStyle(overlayHead).marginBottom,
                ); // overflow auto has undesired side effects
                this.overlayBodyHeight =
                    overlayContent.clientHeight -
                    overlayHead.clientHeight -
                    margin;
                const overlayBodyRule = this._sheet.getRule('overlayBody');
                overlayBodyRule.prop('height', `${this.overlayBodyHeight}px`);
            }
        });
    }

    private _showAtLeastOnePillar(): void {
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        this._modalRef && this._modalRef.dismiss();
        this._modalRef = this._modalService.open(
            this._atLeastOnePillarDialogTpl,
            { centered: true },
        );
        // eslint-disable-next-line no-return-assign
        from(this._modalRef.result).pipe(
            finalize(() => (this._modalRef = null)),
        );
    }

    onCloseOverlay(): void {
        this.gridSelection = null;
        this._store.dispatch(rootClearSelectedVisualItem());
        if (this.state && this.state.presentAs === PresentationView.TREND) {
            this._layout();
        }
    }

    onPeriodTypeChange($event: PeriodType): void {
        this._store.dispatch(periodSelectPeriodType($event));
    }

    onEventDateChange($event: Date): void {
        this._store.dispatch(periodSelectEventDate($event));
    }

    onLevelOptionSelectionChange(
        $event: LevelOptionSelectionChangeEvent,
    ): void {
        this.onCloseOverlay();
        this._store.dispatch(levelOptionSelect($event.selected));
    }

    onVisualItemClick($event: VisualItemClickEvent): void {
        this._store.dispatch(
            rootSelectVisualItem(
                $event.viewPillar,
                $event.viewMetric,
                $event.metric,
                $event.dataMetric,
            ),
        );
    }

    onPresentationSelectionChange($event: PresentationView): void {
        this._store.dispatch(rootSelectPresentationView($event));
    }

    onPathChange($event: ConfigPath): void {
        this._store.dispatch(rootSelectPath($event));
    }

    onPillarsSelectionChange($event: CustomizePillarsInsightChangeEvent): void {
        this._store.dispatch(rootSelectPillars($event.selected));
    }

    onColumnsSelectionChange($event: CustomizeColumnsInsightChangeEvent): void {
        this._store.dispatch(rootSelectOptionalColumns($event.selected));
    }

    onMetricsSelectionChange($event: CustomizeMetricsInsightChangeEvent): void {
        this._store.dispatch(rootSelectMetrics($event.selected));
    }

    onToggleTrendFirstGridColumn(): void {
        this._store.dispatch(
            rootSetTrendHideFirstGridColumn(
                !this.state.trendHideFirstGridColumn,
            ),
        );
    }

    onTrendPeriodTypeChange($event: PeriodType): void {
        this._store.dispatch(trendSelectPeriodType($event));
    }

    onGridNewTabClick($event: GridInsightNewTabClickEvent): void {
        this.addNewTab.emit($event.tab);
    }

    onGridSelectionChange($event: GridInsightSelectionChangeEvent): void {
        this.gridSelection = $event.selected;
        if (this.state && this.state.presentAs === PresentationView.TREND) {
            this._layout();
        }
    }

    onExportGridClick(): void {
        this._store.dispatch(gridExport('pi_data.xlsx'));
    }
}
